import config from '../config'
import {OutfitImage} from "./outfit-image";
import {Collection, CollectionOrder} from "./models";
import {OutfitDetail} from "./outfit-detail";
import {getCountryService, ICountryService} from "../services/country/country-service";
import {CutoutImage} from "./cutout-image";

export class SpecialCollection extends HTMLElement {
  private collectionId: string;
  private collection: Collection;
  private cs: ICountryService;
  private imageObserver: IntersectionObserver;

  private cutoutKeys: Map<string, string>;

  constructor() {
    super();
    console.debug('specialsApiUrl is: ', config.specialsApiUrl);
    this.cs = getCountryService();
    this.updateCollection = this.updateCollection.bind(this)
    this.outfitImageUpdateCallback = this.outfitImageUpdateCallback.bind(this)
    this.onImageIntersecting = this.onImageIntersecting.bind(this)
  }

  debounce<T extends unknown[], U>(
    callback: (...args: T) => PromiseLike<U> | U,
    wait: number
  ) {
    let timer: ReturnType<typeof setTimeout>;
    let boundCallback = callback.bind(this)

    return (...args: T): Promise<U> => {
      clearTimeout(timer);
      return new Promise((resolve) => {
        timer = setTimeout(() => resolve(boundCallback(...args)), wait);
      });
    };
  }

  updateViewDebounce = this.debounce(this.updateView, 250)

  async connectedCallback() {
    this.collectionId = this.getAttribute('collection-id') || this.collectionId;
    const frame = this.getAttribute('detail-image-frame');
    this.imageObserver = new IntersectionObserver(this.onImageIntersecting, {rootMargin: "250%"})
    const shadowRoot = this.attachShadow({mode: 'open'})
    shadowRoot.innerHTML = `<slot></slot><outfit-detail frame="${frame}"></outfit-detail>`;

    if (this.collectionId) {
      await this.updateCollection();
    }
  }

  onImageIntersecting(elements: IntersectionObserverEntry[]) {
    for (const element of elements) {
      if (element.isIntersecting) {
        this.imageObserver.unobserve(element.target);
        if (element.target instanceof CutoutImage) {
          element.target.renderCutoutImage();
        } else {
          (element.target as OutfitImage).renderOutfitImage();
        }
      }
    }
  }

  static get observedAttributes() { return ['collection-id']; }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'collection-id') {
      this.collectionId = newValue;
    }
    if (this.collectionId) {
      await this.updateCollection();
    }
  }

  outfitImageUpdateCallback(element: OutfitImage) {
    const specialCollection = this
    return function () {
      if (specialCollection.collection) {
        specialCollection.updateViewDebounce(specialCollection.collection)
      }
    }
  }

  private async updateCollection() {
    const collection = await this.loadCollection(this.collectionId, (this.cs?.getCountryCodeFromLocation()));
    if (!collection) {
      return;
    }

    this.collection = collection;
    this.cutoutKeys = collection.getCutouts();
    this.updateView(collection);
  }

  private updateView(collection: Collection) {
    const outfitImageElements = Array.from(this.querySelectorAll<OutfitImage>('outfit-image'));
    const filteredOutfits = outfitImageElements.filter(o => collection.outfits.find(co => co.position === o.position));
    filteredOutfits.forEach((outfitImage: OutfitImage) => {
      outfitImage.setOutfit(collection);
      outfitImage.setDescription();
      this.imageObserver.observe(outfitImage);
    })

    const cutoutImageElements = Array.from(this.querySelectorAll<CutoutImage>('cutout-image'));
    cutoutImageElements.forEach((cutoutImage: CutoutImage) => {
      const cutoutImageKey = this.cutoutKeys.get(cutoutImage.getMapKey())
      cutoutImage.setCutout(cutoutImageKey);
      this.imageObserver.observe(cutoutImage)
    })

    const collectionOrder = new CollectionOrder(filteredOutfits.filter((outfitImage: OutfitImage) => !outfitImage.isHidden));
    this.shadowRoot.querySelector<OutfitDetail>('outfit-detail').setCollectionOrder(collectionOrder);
  }

  async loadCollection(collectionId: string, countryCode: string): Promise<Collection> {
    const response = await fetch(`${config.specialsApiUrl}/csp/products/public/collection/products?country=${countryCode}&ids=${collectionId}`,
      {
        credentials: 'omit',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
    const collections = await response.json();
    if (collections.length == 0) {
      console.debug("collection not found");
      return null;
    }

    const collection = new Collection (collections.find(col => col.id === this.collectionId));
    console.debug('loadedCollection', collection);
    return collection;
  }
}

window.customElements.define('special-collection', SpecialCollection);
